import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  ArrowRight,
  MapPin,
  Apple,
  Play,
  Trophy,
  CheckCircle2,
  ChevronRight,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import { CASE_STUDIES_FULL, getCaseStudyBySlug } from "../data/caseStudies";

export default function CaseStudyDetailPage() {
  const { slug } = useParams();
  const c = getCaseStudyBySlug(slug);

  if (!c) {
    return (
      <>
        <Navbar />
        <main className="pt-32 pb-32 text-center">
          <p className="text-overline text-electric mb-3">404</p>
          <h1 className="font-display text-4xl font-extrabold text-charcoal">
            Case study not found
          </h1>
          <Link
            to="/case-studies"
            className="mt-6 inline-flex items-center gap-2 bg-electric text-white px-5 py-3 font-semibold"
          >
            ← Back to case studies
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const url = `https://lumotis.com/case-studies/${c.slug}`;
  const related = CASE_STUDIES_FULL.filter((x) => x.slug !== c.slug).slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{`${c.title} — ${c.appName} Case Study | Lumotis`}</title>
        <meta name="description" content={c.summary} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={`${c.title} · Lumotis case study`} />
        <meta property="og:description" content={c.summary} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
      </Helmet>
      <Navbar />
      <main data-testid="case-detail-page">
        {/* HERO */}
        <section className="relative pt-28 md:pt-32 pb-16 md:pb-20 bg-charcoal text-white overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern-dark opacity-40 pointer-events-none" />
          <div className="container relative max-w-5xl">
            <nav
              aria-label="Breadcrumb"
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 mb-6"
            >
              <Link to="/" className="hover:text-electric">
                Home
              </Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/case-studies" className="hover:text-electric">
                Case Studies
              </Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-white truncate max-w-[40%]">
                {c.appName}
              </span>
            </nav>
            <p className="text-overline text-electric mb-3">{c.category}</p>
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-balance"
              data-testid="case-detail-title"
            >
              {c.title}
            </motion.h1>
            <p className="mt-5 text-lg text-zinc-300 max-w-2xl leading-relaxed">
              {c.summary}
            </p>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
              {c.results.map((r) => (
                <div key={r.label} className="bg-charcoal-900 p-5">
                  <p className="font-display text-3xl md:text-4xl font-black text-electric leading-none">
                    {r.value}
                  </p>
                  <p className="mt-2 text-xs uppercase tracking-widest text-zinc-400 leading-snug">
                    {r.label}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-400">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> {c.market}
              </span>
              <span className="inline-flex items-center gap-1.5">
                {c.platform !== "android" && <Apple className="w-3.5 h-3.5" />}
                {c.platform !== "ios" && <Play className="w-3.5 h-3.5" />}
                {c.platform === "both" ? "iOS + Android" : c.platform === "ios" ? "iOS" : "Android"}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Trophy className="w-3.5 h-3.5 text-electric" /> {c.duration}
              </span>
            </div>
          </div>
        </section>

        {/* CHALLENGE + APPROACH */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container max-w-5xl grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="text-overline text-electric mb-3">01 — The Challenge</p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-charcoal leading-tight">
                What we walked into.
              </h2>
              <p className="mt-5 text-zinc-700 leading-relaxed text-[15px]">
                {c.challenge}
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {c.services.map((s) => (
                  <span
                    key={s}
                    className="text-xs px-3 py-1.5 border border-zinc-300 bg-zinc-50 text-charcoal font-medium"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="text-overline text-electric mb-3">02 — The Approach</p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-charcoal leading-tight mb-6">
                What we did, step by step.
              </h2>
              <ol className="space-y-4">
                {c.approach.map((step, i) => (
                  <li
                    key={i}
                    className="flex gap-4 p-4 border border-zinc-200 bg-white"
                  >
                    <span className="font-display text-xl font-black text-electric w-8 flex-shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-[15px] text-zinc-700 leading-relaxed">
                      {step}
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* QUOTE */}
        <section className="py-16 md:py-20 bg-zinc-50 border-y border-zinc-200">
          <div className="container max-w-4xl text-center">
            <p className="text-overline text-electric mb-4">Client voice</p>
            <p className="font-display text-3xl md:text-4xl text-charcoal leading-snug font-bold text-balance">
              "{c.quote.text}"
            </p>
            <p className="mt-6 text-zinc-500 text-sm">— {c.quote.author}</p>
          </div>
        </section>

        {/* RESULTS DETAIL */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container max-w-5xl">
            <p className="text-overline text-electric mb-3">03 — The Results</p>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-charcoal leading-tight mb-10">
              Audited numbers, not vanity metrics.
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {c.results.map((r, idx) => (
                <li
                  key={r.label}
                  className="flex items-start gap-4 p-6 border border-zinc-200"
                  data-testid={`case-result-${idx}`}
                >
                  <CheckCircle2
                    className="w-6 h-6 text-electric flex-shrink-0 mt-0.5"
                    strokeWidth={2.5}
                  />
                  <div>
                    <p className="font-display text-3xl md:text-4xl font-black text-charcoal leading-none">
                      {r.value}
                    </p>
                    <p className="mt-2 text-sm text-zinc-600 leading-snug">
                      {r.label}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-12 flex flex-wrap items-center justify-between gap-4">
              <Link
                to="/case-studies"
                className="inline-flex items-center gap-2 text-charcoal font-semibold hover:text-electric"
              >
                <ArrowLeft className="w-4 h-4" /> All case studies
              </Link>
              <Link
                to="/free-aso-audit"
                className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3 font-semibold hover:bg-electric-400 shadow-lg rounded-xl"
              >
                Become the next case study <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Related */}
        <section className="py-16 md:py-20 bg-zinc-50 border-y border-zinc-200">
          <div className="container">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-charcoal mb-10">
              Related case studies
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/case-studies/${r.slug}`}
                  className="group bg-white border border-zinc-200 hover:border-charcoal transition-colors p-6 flex flex-col"
                >
                  <span className="text-overline text-electric mb-3">
                    {r.category}
                  </span>
                  <p className="font-display text-3xl font-black text-electric leading-none mb-2">
                    {r.metric}
                  </p>
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-4">
                    {r.metricLabel}
                  </p>
                  <h3 className="font-display text-lg font-bold text-charcoal leading-snug">
                    {r.title}
                  </h3>
                  <span className="mt-auto pt-5 text-xs font-semibold text-charcoal group-hover:text-electric inline-flex items-center gap-1">
                    Read case study <ArrowUpRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
